import type { ChunkInfo, Geometry, XZ } from './types';

/** Planar polygon area in m² (shoelace). World metres are already planar — no geodesic maths. */
export function polygonArea(points: XZ[]): number {
  let sum = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    sum += (points[j][0] + points[i][0]) * (points[j][1] - points[i][1]);
  }
  return Math.abs(sum) / 2;
}

export function polylineLength(points: XZ[], closed = false): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    len += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
  }
  if (closed && points.length > 2) {
    const a = points[points.length - 1],
      b = points[0];
    len += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return len;
}

/** Area-weighted centroid; falls back to the vertex mean for degenerate polygons. */
export function polygonCentroid(points: XZ[]): { x: number; z: number } {
  let a = 0,
    cx = 0,
    cz = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const cross = points[j][0] * points[i][1] - points[i][0] * points[j][1];
    a += cross;
    cx += (points[j][0] + points[i][0]) * cross;
    cz += (points[j][1] + points[i][1]) * cross;
  }
  if (Math.abs(a) < 1e-9) {
    const n = Math.max(1, points.length);
    return {
      x: points.reduce((s, p) => s + p[0], 0) / n,
      z: points.reduce((s, p) => s + p[1], 0) / n,
    };
  }
  return { x: cx / (3 * a), z: cz / (3 * a) };
}

/** A representative point for labels, badges and "jump to". */
export function geometryAnchor(g: Geometry): { x: number; z: number } | null {
  switch (g.type) {
    case 'point':
    case 'circle':
    case 'rect':
      return { x: g.x, z: g.z };
    case 'polygon':
      return g.points.length ? polygonCentroid(g.points) : null;
    case 'polyline':
      return g.points.length ? { x: g.points[0][0], z: g.points[0][1] } : null;
    default:
      return null;
  }
}

export function rectCorners(g: { x: number; z: number; w: number; d: number }): XZ[] {
  const hw = g.w / 2,
    hd = g.d / 2;
  return [
    [g.x - hw, g.z - hd],
    [g.x + hw, g.z - hd],
    [g.x + hw, g.z + hd],
    [g.x - hw, g.z + hd],
  ];
}

export interface GeometryStats {
  area?: number;
  perimeter?: number;
  length?: number;
  vertices?: number;
}

export function geometryStats(g: Geometry): GeometryStats {
  switch (g.type) {
    case 'circle':
      return { area: Math.PI * g.r * g.r, perimeter: 2 * Math.PI * g.r };
    case 'rect':
      return { area: g.w * g.d, perimeter: 2 * (g.w + g.d) };
    case 'polygon':
      return {
        area: polygonArea(g.points),
        perimeter: polylineLength(g.points, true),
        vertices: g.points.length,
      };
    case 'polyline':
      return { length: polylineLength(g.points), vertices: g.points.length };
    default:
      return {};
  }
}

/** Which authored chunk contains (x, z). Chunk layout is per-map data (Startland 512 m 3×3, Mainland 1024 m 10×10). */
export function chunkAt(
  x: number,
  z: number,
  origin: { x: number; z: number },
  chunkSize: number,
  chunks: ChunkInfo[],
): ChunkInfo | null {
  const cx = Math.floor((x - origin.x) / chunkSize);
  const cz = Math.floor((z - origin.z) / chunkSize);
  return chunks.find((c) => c.cx === cx && c.cz === cz) ?? null;
}

export function pointInPolygon(x: number, z: number, points: XZ[]): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, zi] = points[i];
    const [xj, zj] = points[j];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}
